import { FC, useContext, useMemo, useState } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderHubFilters, ProviderSearchByEnum, ProviderViewByEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import ProviderHubGenerateReportFlyout from "@app/projects/network-assessment/provider-hub/ProviderHubGenerateReportFlyout";
import { QESButton } from "@app/shared/qes-standards/Button";

// The report flyout opens over the IAP drawer (both are portals at the same z-index), so give it a stable id the
// SCSS can target to lift it — and its backdrop — above the drawer (see _new-flyout dependent rules).
const REPORT_FLYOUT_PANEL_ID = "hospital-iap-report-flyout";

// "Generate Report" action for the Institutionally Affiliated Providers drawer. Reuses the standard Provider Hub
// report flyout, but scopes it to the single hospital being viewed: view-by Hospital + search-by CCN resolves the
// backend Hospital extract to just this facility's Type-1 institutionally-affiliated providers. All other filters are
// reset to their defaults so the report is NOT limited by the drawer's on-screen filters (per the ticket).
export const HospitalIapReportButton: FC = () => {
  const { initialFilterState, selectedHospital } = useContext(ProviderHubContext);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);

  const reportFilters = useMemo<ProviderHubFilters>(() => ({
    ...initialFilterState,
    searchByInput: selectedHospital?.ccn ?? "",
    searchByOption: ProviderSearchByEnum.CCN,
    viewByOption: ProviderViewByEnum.Hospital,
  }), [initialFilterState, selectedHospital?.ccn]);

  if (!selectedHospital) return null;

  return (
    <>
      <QESButton
        qesType="primary"
        qesTestId="hospital-iap-drawer__generate-report"
        onClick={() => setIsReportModalOpen(true)}
      >
        Generate Report
      </QESButton>
      <ProviderHubGenerateReportFlyout
        isModalOpen={isReportModalOpen}
        setIsModalOpen={setIsReportModalOpen}
        filters={reportFilters}
        panelId={REPORT_FLYOUT_PANEL_ID}
      />
    </>
  );
};
